import { User, UserManager } from "oidc-client";

// eslint-disable-next-line
type AnyType = any;

const HTTP_STATUS_ERROR_LOGIN = 403;
const HTTP_STATUS_ERROR_UPDATE_TOKEN = 401;

export interface LoginParams {
  /**
   * サイレント認証が成功した場合に呼び出されます
   * @callback success
   * @param {string} name - ユーザー名
   */
  success: (name: string) => void;
  /**
   * 認証画面呼び出しでcatchした場合に呼び出されます
   * @callback fail
   * @param status - httpステータス
   */
  fail: (status: number) => void;
}

export interface LogInCallbackParams {
  /**
   * 認証画面での認証が成功した場合に呼び出されます
   * @callback success
   * @param {string} name - ユーザー名
   */
  success: (name: string) => void;
  /**
   * 認証画面での認証でcatchした場合に呼び出されます
   * @callback fail
   * @param status - httpステータス
   */
  fail: (status: number) => void;
}

export interface AuthError {
  /**
   * メッセージID
   */
  id: undefined;
  /**
   * エラーメッセージ
   */
  message: string;
  /**
   * httpステータス
   */
  status: number;
}

export interface AuthErrorResponce {
  /**
   * 認証エラー
   */
  auth: AuthError;
}

export interface TokenParams {
  /**
   * トークン取得が成功した場合に呼び出されます
   * @callback success
   * @param {string} token - トークン
   */
  success: (token: string) => void;
  /**
   * トークン取得が失敗した場合に呼び出されます
   * @callback fail
   * @param {AuthErrorResponce} error - エラーオブジェクト
   */
  fail: (error: AuthErrorResponce) => void;
}

function getUpdateTokenError(message: string): AuthErrorResponce {
  return {
    auth: {
      id: undefined,
      message: message,
      status: HTTP_STATUS_ERROR_UPDATE_TOKEN,
    },
  };
}

/**
 * UserManager.jsの_signoutStart参照
 */
function signoutStart(manager: UserManager, url: string, id_token: string): Promise<void> { 
  return manager.settings.redirectNavigator
    .prepare()
    .then((handle: AnyType) => {
      return manager.removeUser().then(() => {
        return manager.createSignoutRequest().then((value) => {
          return handle.navigate({
            url: value.url + "?post_logout_redirect_uri=" + url + "&id_token_hint=" + id_token,
          });
        });
      });
    });
}

class AuthService {
  private readonly URL = "https://www.ai-ert3.yokogawa.com";
  private readonly LOGOUT_URL = this.URL + "/logout";
  private manager: UserManager;

  constructor() {
    const config = {
      authority: "https://api.msa.apps.yokogawa.com/idpt-authz/auth/realms/ERT3-AI",
      client_id: "ERT3-AI",
      client_secret: "dya7MiUib48Sxo7yh51UOO6QCOG6IuTu",
      redirect_uri: this.URL + "/callback",
      silent_redirect_uri: this.URL + "/scallback",
      response_type: "code",
      scope: "openid email profile roles",
      automaticSilentRenew: true,
      silentRequestTimeout: 3000,
    };
    this.manager = new UserManager(config);
  }

  /**
   * ログイン処理を行います
   * @param {LoginParams} param - 認証パラメータ
   */
  login(param: LoginParams): void {
    this.manager
      .signinSilent({
        state: {
          returnUrl: window.location.pathname,
        },
      })
      .then((user: User) => {
        //console.log(user);
        param.success(user.profile.name ? user.profile.name : "");
      })
      .catch(() => {
        this.manager
          .signinRedirect({
            state: {
              returnUrl: window.location.pathname,
            },
          })
          .then(() => {
            // 認証サーバーのログイン画面に移動
          })
          .catch((error) => {
            console.log({ error: error });
            param.fail(HTTP_STATUS_ERROR_LOGIN);
          });
      });
  }

  /**
   * 認証画面からのコールバック処理を行います
   * @param {LogInCallbackParams} param - 認証コールバックパラメータ
   */
  logInCallback(param: LogInCallbackParams): void {
    const mgr = new UserManager({
      response_mode: "query",
    });
    mgr
      .signinRedirectCallback()
      .then((user: User) => {
        param.success(user.profile.name ? user.profile.name : "");
      })
      .catch((error: Error) => {
        console.log({ error: error });
        param.fail(HTTP_STATUS_ERROR_LOGIN);
      });
  }

  /**
   * ログアウトします
   */
  logout(): void {
    this.getIdToken({
      success: (id_token) => {
        signoutStart(this.manager, this.LOGOUT_URL, id_token).catch((error) => {
          console.log(error);
        });
      },
      fail: (error) => {
        console.log({ error: error });
      },
    });
  }

  /**
   * トークンを取得します
   * @param {TokenParams} param - トークンパラメータ
   */
  getToken(param: TokenParams): void {
    this.manager
      .getUser()
      .then((user) => {
        if (user) {
          const token = user.access_token;
          param.success(token);
        } else {
          this.updateToken(param);
        }
      })
      .catch(() => {
        this.updateToken(param);
      });
  }

  /**
   * トークンを更新します
   * @param {TokenParams} param - トークンパラメータ
   */
  updateToken(param: TokenParams) {
    this.manager
      .signinSilent()
      .then((user: User) => {
        const token = user.access_token;
        param.success(token);
      })
      .catch((error: Error) => {
        console.log({ error: error });
        param.fail(getUpdateTokenError(error.message));
      });
  }

  /**
   * トークンを取得します
   * @param {IdTokenParams} param - トークンパラメータ
   */
  getIdToken(param: TokenParams): void {
    this.manager
      .getUser()
      .then((user) => {
        if (user) {
          const id_token = user.id_token;
          param.success(id_token);
        } else {
          this.updateIdToken(param);
        }
      })
      .catch(() => {
        this.updateIdToken(param);
      });
  }

  /**
   * トークンを更新します
   * @param {IdTokenParams} param - トークンパラメータ
   */
  updateIdToken(param: TokenParams) {
    this.manager
      .signinSilent()
      .then((user: User) => {
        const id_token = user.id_token;
        param.success(id_token);
      })
      .catch((error: Error) => {
        console.log({ error: error });
        param.fail(getUpdateTokenError(error.message));
      });
  }

  /**
   * ユーザ名を取得します
   * @param {LoginParams} param 
   */
  getUserName(param: LoginParams) {
    this.manager
      .getUser()
      .then((user) => {
        if (user) {
          const name = user.profile.name;
          param.success(name !== undefined ? name : "");
        }
      })
      .catch((error: Error) => {
        console.log({ error: error });
        param.fail(HTTP_STATUS_ERROR_LOGIN);
      })
  }

  /**
   * バックグラウンドのtoken更新を停止します
   */
  stopSilentRenew() {
    this.manager.stopSilentRenew();
  }
}

/**
 * 認証サービス
 */
export default AuthService;
